import { useEffect, useState, type JSX } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';

/**
 * Registrar passkey management — the settings-style card on the registrar
 * dashboard (the LodgementFormToggle pattern). Enrol the current device,
 * rename, revoke. Sign-in itself lives on the login page; the email OTP is
 * the permanent recovery path, so enrolment never risks lockout.
 */

interface PasskeySummary {
  credentialId: string;
  deviceLabel: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  backedUp: boolean;
  deviceType: string | null;
}

const CARD: React.CSSProperties = {
  background: 'white',
  border: '1px solid #e0e0d8',
  borderRadius: 8,
  padding: '20px 24px',
  marginBottom: 12,
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Best-effort device name so a fresh enrolment is recognisable in the list. */
function defaultDeviceLabel(): string {
  const ua = navigator.userAgent;
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Mac/.test(ua)
        ? 'Mac'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Android/.test(ua)
            ? 'Android'
            : 'Device';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'browser';
  return `${os} · ${browser}`;
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as T;
  if (!res.ok) throw Object.assign(new Error(JSON.stringify(data)), { status: res.status, data });
  return data;
}

export function PasskeysCard({
  onClose,
  hideTitle,
}: { onClose?: () => void; hideTitle?: boolean } = {}): JSX.Element {
  const supported = !!window.PublicKeyCredential;
  const [passkeys, setPasskeys] = useState<PasskeySummary[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [needsSignIn, setNeedsSignIn] = useState(false);

  const load = () => {
    setError(null);
    setLoadFailed(false);
    setNeedsSignIn(false);
    fetch('/api/vetting/passkeys', { credentials: 'include' })
      .then(async (res) => {
        if (res.ok) return (await res.json()) as { passkeys: PasskeySummary[] };
        // Honest, status-specific errors. The old catch-all hid a lapsed session
        // (401) behind "Could not load passkeys", which read as a broken feature.
        const message =
          res.status === 401
            ? 'Your session has expired — please sign in again.'
            : res.status === 403
              ? 'Passkey management is registrar-only.'
              : res.status === 503
                ? 'Passkeys are temporarily unavailable — please try again shortly.'
                : 'Could not load passkeys (server error).';
        throw Object.assign(new Error(message), { status: res.status });
      })
      .then((data) => setPasskeys(data.passkeys))
      .catch((e: unknown) => {
        const status = (e as { status?: number }).status;
        setError((e as { message?: string }).message ?? 'Could not load passkeys.');
        setLoadFailed(true);
        setNeedsSignIn(status === 401);
      });
  };
  useEffect(load, []);

  const handleEnrol = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { options } = await apiPost<{ options: PublicKeyCredentialCreationOptionsJSON }>(
        '/api/vetting/passkeys/register-options',
        {},
      );

      let attestation;
      try {
        attestation = await startRegistration({ optionsJSON: options });
      } catch (err) {
        const name = (err as Error | null)?.name;
        if (name === 'InvalidStateError') {
          setError('This device already has a passkey for your account.');
        } else if (name !== 'NotAllowedError') {
          setError('Enrolment was interrupted. Please try again.');
        }
        setBusy(false);
        return;
      }

      const result = await apiPost<{ ok: boolean; passkeys: PasskeySummary[] }>(
        '/api/vetting/passkeys/register-verify',
        { response: attestation, label: defaultDeviceLabel() },
      );
      setPasskeys(result.passkeys);
      setNotice('Passkey added. You can now sign in on this device with a passkey.');
    } catch (e) {
      const err = e as { data?: { error?: string } };
      setError(
        err.data?.error === 'credential_exists'
          ? 'This device already has a passkey for your account.'
          : 'Could not add the passkey. Please try again.',
      );
    }
    setBusy(false);
  };

  const handleRename = async (credentialId: string) => {
    const label = renameValue.trim();
    if (!label) return;
    setError(null);
    try {
      await apiPost('/api/vetting/passkeys/rename', { credentialId, label });
      setPasskeys(
        (list) =>
          list?.map((p) => (p.credentialId === credentialId ? { ...p, deviceLabel: label } : p)) ??
          null,
      );
      setRenaming(null);
    } catch (e) {
      // 404 = the passkey was already removed elsewhere (another tab/device);
      // resync rather than show a misleading failure.
      if ((e as { status?: number }).status === 404) {
        setRenaming(null);
        load();
        setError('That passkey no longer exists — the list has been refreshed.');
      } else {
        setError('Rename failed. Please try again.');
      }
    }
  };

  const handleRevoke = async (p: PasskeySummary) => {
    const label = p.deviceLabel ?? 'this device';
    const lastOne = (passkeys?.length ?? 0) === 1;
    const message = lastOne
      ? `Remove the passkey for ${label}? This is your ONLY passkey — you'll be back to email codes until you enrol another device.\n\nNote: removing the passkey does not end a session already open on that device — use "Sign out all devices" below for that. The passkey just can't be used to sign in again.`
      : `Remove the passkey for ${label}? That device can no longer sign in with a passkey.\n\nNote: removing the passkey does NOT end a session already open on that device — use "Sign out all devices" below for that.`;
    if (!window.confirm(message)) return;
    setError(null);
    try {
      await apiPost('/api/vetting/passkeys/revoke', { credentialId: p.credentialId });
      setPasskeys((list) => list?.filter((x) => x.credentialId !== p.credentialId) ?? null);
      setNotice(null);
    } catch (e) {
      // 404 = already revoked elsewhere; treat as success and drop the stale row.
      if ((e as { status?: number }).status === 404) {
        setPasskeys((list) => list?.filter((x) => x.credentialId !== p.credentialId) ?? null);
        setNotice('That passkey had already been removed.');
      } else {
        setError('Could not remove the passkey. Please try again.');
      }
    }
  };

  return (
    <div style={CARD}>
      {!hideTitle && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 4,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
            Passkeys — sign in without a code
          </div>
          {onClose && (
            <button className="btn btn-ghost btn-sm" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      )}
      <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
        Passkeys use Touch ID / Face ID on a trusted device and can't be phished. Your email login
        code always keeps working as the backup.
      </p>

      {!supported && (
        <p style={{ fontSize: 13, color: 'var(--text2)' }}>
          This browser doesn't support passkeys.
        </p>
      )}

      {error && (
        <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>
          {error}
          {needsSignIn ? (
            <button
              className="btn btn-ghost btn-sm"
              style={{ marginLeft: 8 }}
              onClick={() => {
                window.location.href = '/auth/login';
              }}
            >
              Sign in again
            </button>
          ) : loadFailed ? (
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }} onClick={load}>
              Retry
            </button>
          ) : null}
        </div>
      )}
      {notice && <div style={{ color: '#1e4d3f', fontSize: 13, marginBottom: 12 }}>{notice}</div>}

      {passkeys !== null && passkeys.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {passkeys.map((p) => (
            <div
              key={p.credentialId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 0',
                borderBottom: '1px solid #f0ebde',
                fontSize: 13,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                {renaming === p.credentialId ? (
                  <span style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="input"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      maxLength={60}
                      style={{ fontSize: 13, padding: '4px 8px', maxWidth: 220 }}
                      // eslint-disable-next-line jsx-a11y/no-autofocus -- deliberate: focused after the user explicitly starts renaming a passkey
                      autoFocus
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => void handleRename(p.credentialId)}
                    >
                      Save
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setRenaming(null)}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                    {p.deviceLabel ?? 'Unnamed device'}
                    {p.backedUp && (
                      <span className="pill pill-success" style={{ marginLeft: 8 }}>
                        synced
                      </span>
                    )}
                  </span>
                )}
                <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 2 }}>
                  Added {fmtDate(p.createdAt)} · Last used {fmtDate(p.lastUsedAt)}
                </div>
              </div>
              {renaming !== p.credentialId && (
                <>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setRenaming(p.credentialId);
                      setRenameValue(p.deviceLabel ?? '');
                    }}
                  >
                    Rename
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => void handleRevoke(p)}>
                    Remove
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {passkeys !== null && passkeys.length < 2 && supported && (
        <p style={{ fontSize: 12, color: '#8a6d1a', marginBottom: 12 }}>
          {passkeys.length === 0
            ? 'No passkeys yet. Add this device to start; then add your phone too, so losing one device never locks you out.'
            : 'One device enrolled. Add a second (e.g. your phone) before relying on passkeys day-to-day.'}
        </p>
      )}

      {supported && (
        <button
          className="btn btn-primary"
          onClick={() => void handleEnrol()}
          disabled={busy || passkeys === null}
        >
          {busy ? 'Waiting for this device…' : 'Add this device'}
        </button>
      )}
    </div>
  );
}
